import {
  Download,
  FileVideo,
  Filter,
  Loader2,
  Merge,
  MoreVertical,
  Play,
  Plus,
  Upload,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Id } from "@cvx/_generated/dataModel";
import StatusBadge from "@/components/Generic/StatusBadge";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { VideoFeedbackDetail } from "./VideoFeedbackDetail";
import { VideoProject } from "@/types/types";
import { api } from "@cvx/_generated/api";
import { formatDistance } from "date-fns";
import { useAccount } from "@/state/useAccount";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

interface PartnershipFeedbackVideosProps {
  contractId: Id<"creatorContracts">;
  initialProjectId?: string;
  initialVersionId?: string;
  onNavigate?: (projectId?: string, versionId?: string) => void;
}

const MergeProjectDialog = ({
  open,
  onOpenChange,
  sourceProject,
  allProjects,
  onMerge,
  isMerging,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceProject: VideoProject;
  allProjects: VideoProject[];
  onMerge: (targetProjectId: string) => void;
  isMerging: boolean;
}) => {
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);

  // Reset selected target when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTargetId(null);
    }
  }, [open]);

  // Filter out the source project from available targets
  const availableTargets = allProjects.filter(
    (p) => p.projectId !== sourceProject.projectId,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Merge Project</DialogTitle>
          <DialogDescription>
            Select a project to merge "{sourceProject.title}" into. All versions
            from the source project will become versions of the target project.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-primary">
              Target Project
            </label>
            <Select
              value={selectedTargetId || undefined}
              onValueChange={setSelectedTargetId}
              disabled={isMerging}
            >
              <SelectTrigger className="w-full mt-1">
                <SelectValue placeholder="Select a project..." />
              </SelectTrigger>
              <SelectContent>
                {availableTargets.length === 0 ? (
                  <SelectItem value="no-projects" disabled>
                    No projects available
                  </SelectItem>
                ) : (
                  availableTargets.map((project) => (
                    <SelectItem
                      key={project.projectId}
                      value={project.projectId}
                    >
                      {project.title} ({project.versions.length} version
                      {project.versions.length !== 1 ? "s" : ""})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isMerging}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedTargetId) {
                onMerge(selectedTargetId);
              }
            }}
            disabled={!selectedTargetId || isMerging}
          >
            {isMerging ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Merging...
              </>
            ) : (
              <>
                <Merge className="mr-2 h-4 w-4" />
                Merge Projects
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProjectBrowser = ({
  projects,
  selectedProjectId,
  onSelectProject,
  onCreateProject,
  isAdmin,
  onMergeProject,
}: {
  projects: VideoProject[];
  selectedProjectId: string | null;
  onSelectProject: (project: VideoProject) => void;
  onCreateProject: () => void;
  isAdmin: boolean;
  onMergeProject: (sourceProject: VideoProject) => void;
}) => {
  // Track which dropdown is open to ensure only one is open at a time
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const { creatorId } = useAccount();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [downloadingProjectId, setDownloadingProjectId] = useState<
    string | null
  >(null);
  const [hoveredProjectId, setHoveredProjectId] = useState<string | null>(null);

  // Get all unique statuses from projects
  const allStatuses = Array.from(
    new Set(
      projects
        .map((project) => project.versions[project.versions.length - 1]?.status)
        .filter(Boolean),
    ),
  );

  // Initialize selectedStatuses with all statuses when projects load (only once)
  useEffect(() => {
    if (allStatuses.length > 0 && !hasInitialized) {
      setSelectedStatuses(allStatuses);
      setHasInitialized(true);
    }
  }, [allStatuses.length, hasInitialized]);

  // Filter projects based on selected statuses
  const filteredProjects = projects.filter((project) => {
    const latestVersion = project.versions[project.versions.length - 1];
    return selectedStatuses.includes(latestVersion.status);
  });

  const handleStatusToggle = (status: string, checked: boolean) => {
    if (checked) {
      setSelectedStatuses((prev) => [...prev, status]);
    } else {
      setSelectedStatuses((prev) => prev.filter((s) => s !== status));
    }
  };

  const handleSelectAll = () => {
    setSelectedStatuses(allStatuses);
  };

  const handleSelectNone = () => {
    setSelectedStatuses([]);
  };

  const handleDownloadVideo = async (
    project: VideoProject,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation(); // Prevent project selection when clicking download

    if (downloadingProjectId === project.projectId) return; // Prevent multiple downloads

    const latestVersion = project.versions[project.versions.length - 1];
    if (!latestVersion?.videoUrl) return;

    setDownloadingProjectId(project.projectId);

    try {
      // Create download link directly from video URL
      const link = document.createElement("a");
      link.href = latestVersion.videoUrl;
      link.download = `${project.title}_v${project.versions.length}.mp4`;
      link.target = "_blank"; // Open in new tab as fallback
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading video:", error);
    } finally {
      setDownloadingProjectId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-primary">Video Projects</h3>
        <div className="flex items-center gap-2">
          {projects.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter ({selectedStatuses.length}/{allStatuses.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                <DropdownMenuSeparator />

                <div className="flex items-center justify-between px-2 py-1">
                  <Button
                    onClick={handleSelectAll}
                    variant="ghost"
                    size="sm"
                    className="text-xs h-6 px-2"
                  >
                    All
                  </Button>
                  <Button
                    onClick={handleSelectNone}
                    variant="ghost"
                    size="sm"
                    className="text-xs h-6 px-2"
                  >
                    None
                  </Button>
                </div>

                <DropdownMenuSeparator />

                {allStatuses.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.includes(status)}
                    onCheckedChange={(checked) =>
                      handleStatusToggle(status, checked)
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <StatusBadge status={status} />
                      <span className="capitalize sr-only">
                        {status.replace("_", " ")}
                      </span>
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <div className="px-2 py-1 text-xs text-muted-foreground">
                  Showing {filteredProjects.length} of {projects.length}{" "}
                  projects
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {creatorId && (
            <Button onClick={onCreateProject} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {filteredProjects.map((project) => {
          const latestVersion = project.versions[project.versions.length - 1];
          const isSelected = selectedProjectId === project.projectId;

          return (
            <div
              key={project.projectId}
              onMouseEnter={() => setHoveredProjectId(project.projectId)}
              onMouseLeave={() => setHoveredProjectId(null)}
              onClick={(e) => {
                // Don't select project if clicking on dropdown menu or if dropdown is open
                if (
                  (e.target as HTMLElement).closest('[role="menu"]') ||
                  openDropdownId === project.projectId
                ) {
                  return;
                }
                onSelectProject(project);
              }}
              className={`block border border-border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg hover:bg-accent/10 hover:border-primary/20 overflow-hidden ${isSelected ? "ring-2 ring-primary border-primary" : ""
                }`}
            >
              {/* Video Thumbnail - 90% of card */}
              <div className="relative aspect-[9/16] w-full">
                <video
                  src={latestVersion.videoUrl as string}
                  className="w-full h-full object-cover bg-secondary"
                  muted
                  loop
                  playsInline
                  crossOrigin="anonymous"
                  preload={
                    hoveredProjectId === project.projectId ? "auto" : "metadata"
                  }
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 rounded-full p-3">
                    <Play className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="absolute top-2 right-2">
                  <StatusBadge status={latestVersion.status} />
                </div>
                <div className="absolute top-2 left-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => handleDownloadVideo(project, e)}
                    disabled={downloadingProjectId === project.projectId}
                    className="bg-black/60 text-white hover:bg-black/80 p-2 h-8 w-8 disabled:opacity-50"
                    title={
                      downloadingProjectId === project.projectId
                        ? "Downloading..."
                        : "Download latest version"
                    }
                  >
                    {downloadingProjectId === project.projectId ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                  {/* Add merge button for admins */}
                  {isAdmin && (
                    <DropdownMenu
                      open={openDropdownId === project.projectId}
                      onOpenChange={(open) =>
                        setOpenDropdownId(open ? project.projectId : null)
                      }
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => e.stopPropagation()}
                          className="bg-black/60 text-white hover:bg-black/80 p-2 h-8 w-8"
                          title="More options"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            setOpenDropdownId(null);
                            onMergeProject(project);
                          }}
                        >
                          <Merge className="mr-2 h-4 w-4" />
                          Merge into another project
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              {/* Project Info - 10% of card */}
              <div className="p-3">
                <h4 className="font-medium text-primary text-sm line-clamp-2 mb-1">
                  {project.title}
                </h4>
                <div className="flex items-center justify-between text-xs text-primary/60">
                  <span>{project.versions.length}v</span>
                  <span>
                    {formatDistance(project.createdAt, new Date(), {
                      addSuffix: true,
                    }).replace(" ago", "")}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state when no filters are selected */}
      {projects && projects.length > 0 && selectedStatuses.length === 0 && (
        <Card className="p-12 text-center">
          <Filter className="h-12 w-12 text-primary/60 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">
            No status filters selected
          </h3>
          <p className="text-sm text-primary/60 mb-4">
            You've deselected all status filters. Please select at least one
            status to view your video projects.
          </p>
          <Button
            onClick={() => setSelectedStatuses(allStatuses)}
            variant="outline"
          >
            <Filter className="h-4 w-4 mr-2" />
            Show All Projects
          </Button>
        </Card>
      )}

      {/* Empty state when no projects exist */}
      {projects && projects.length === 0 && (
        <Card className="p-12 text-center">
          <FileVideo className="h-12 w-12 text-primary/60 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">
            No video projects yet
          </h3>
          <p className="text-sm text-primary/60 mb-4">
            {creatorId ? (
              "Create your first video project to start collaborating with timeline-specific feedback"
            ) : (
              <>
                This is where creators will submit videos for your review and
                feedback.
                <br />
                Once they do, you'll be able to provide timeline-specific
                feedback and approve or request revisions.
              </>
            )}
          </p>
        </Card>
      )}
    </div>
  );
};

export const PartnershipFeedbackVideos = ({
  contractId,
  initialProjectId,
  initialVersionId,
  onNavigate,
}: PartnershipFeedbackVideosProps) => {
  const { creatorId } = useAccount();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: projects, refetch } = useQuery(
    convexQuery(api.creator_internal_videos.getPaginatedCreatorVideoProjects, {
      contractId,
      accountType: creatorId ? "creator" : "brand",
    }),
  );

  // Get current user to check if they're a team member
  const { data: currentUser } = useQuery({
    ...convexQuery(api.app.getCurrentUser, {}),
  });

  // Get contract to check campaign settings
  const { data: contractData } = useQuery({
    ...convexQuery(api.partnerships.getPartnershipHistoricalPerformance, {
      contractId,
      accountType: creatorId ? "creator" : "brand",
    }),
  });

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);

  const { mutateAsync: createProject } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.createCreatorVideoProject,
    ),
  });

  const { mutateAsync: mergeProjects } = useMutation({
    mutationFn: useConvexMutation(
      api.creator_internal_videos.mergeVideoProjects,
    ),
  });

  // Initialize selectedProjectId from URL params
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    initialProjectId || null,
  );
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [sourceProjectForMerge, setSourceProjectForMerge] =
    useState<VideoProject | null>(null);
  const [isMerging, setIsMerging] = useState(false);

  // Update selectedProjectId when initialProjectId changes
  useEffect(() => {
    if (initialProjectId !== undefined) {
      setSelectedProjectId(initialProjectId);
    }
  }, [initialProjectId]);

  // Check if current user is a team member
  const isCreatorGridTeamMember = currentUser
    ? TEAM_USER_IDS.includes(currentUser._id)
    : false;

  // Check if user is admin (team member or app founder)
  const isAdmin = isCreatorGridTeamMember || (!creatorId && !!currentUser);

  // Filter projects based on teamVideoReviewRequired
  const filteredProjects =
    projects?.filter((project) => {
      // If teamVideoReviewRequired is true and user is a founder (not a creator) and not a team member,
      // only show approved video projects
      const campaign = contractData?.campaign;
      if (
        campaign &&
        campaign.teamVideoReviewRequired &&
        !creatorId && // Only filter for founders, not creators
        !isCreatorGridTeamMember
      ) {
        const latestVersion = project.versions[project.versions.length - 1];
        return latestVersion?.status === "approved";
      }
      return true;
    }) || [];

  // Derive the selected project from the filtered projects array to ensure it's always up-to-date
  const selectedProject = selectedProjectId
    ? filteredProjects?.find((p) => p.projectId === selectedProjectId) || null
    : null;

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      try {
        const uploadResponse = uploaded[0].response as {
          storageId: Id<"_storage">;
        };

        await createProject({
          contractId,
          accountType: creatorId ? "creator" : "brand",
          videoStorageId: uploadResponse.storageId,
          title: newProjectTitle,
        });

        toast({
          title: "Project created",
          description: "Your video project has been created successfully",
        });

        setShowNewProjectForm(false);
        setNewProjectTitle("");
      } catch (error) {
        console.error("Error creating project:", error);
        toast({
          title: "Error creating project",
          description: "There was an error creating your project",
          variant: "destructive",
        });
      } finally {
        setIsCreatingProject(false);
      }
    },
    onUploadError: () => {
      toast({
        title: "Error uploading video",
        description: "There was an error uploading your video",
        variant: "destructive",
      });
      setIsCreatingProject(false);
    },
  });

  const handleSelectProject = (project: VideoProject) => {
    setSelectedProjectId(project.projectId);
    // Update URL when project is selected
    onNavigate?.(project.projectId);
  };

  const handleBackToProjects = () => {
    setSelectedProjectId(null);
    // Clear URL params when going back to projects list
    onNavigate?.(undefined, undefined);
  };

  const handleMergeProject = (sourceProject: VideoProject) => {
    setSourceProjectForMerge(sourceProject);
    setMergeDialogOpen(true);
  };

  const handleConfirmMerge = async (targetProjectId: string) => {
    if (!sourceProjectForMerge) return;

    setIsMerging(true);
    try {
      await mergeProjects({
        sourceProjectId: sourceProjectForMerge.projectId,
        targetProjectId: targetProjectId as Id<"creatorVideoProjects">,
        accountType: creatorId ? "creator" : "brand",
      });

      toast({
        title: "Projects merged",
        description: `"${sourceProjectForMerge.title}" has been merged successfully`,
      });

      setMergeDialogOpen(false);
      setSourceProjectForMerge(null);
      refetch();
    } catch (error) {
      console.error("Error merging projects:", error);
      toast({
        title: "Error merging projects",
        description:
          error instanceof Error ? error.message : "Failed to merge projects",
        variant: "destructive",
      });
    } finally {
      setIsMerging(false);
    }
  };

  const handleCreateProject = (files: File[]) => {
    if (!newProjectTitle.trim()) {
      toast({
        title: "Missing title",
        description: "Please provide a title for your project",
        variant: "destructive",
      });
      return;
    }

    if (files.length === 0) {
      toast({
        title: "No video selected",
        description: "Please select a video file to upload",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingProject(true);
    startUpload(files);
  };

  // If a project is selected, show the video detail interface
  if (selectedProject) {
    return (
      <VideoFeedbackDetail
        project={selectedProject}
        allLinkedVideos={
          filteredProjects
            ? filteredProjects
              .map((p) => p.contractVideoId)
              .filter((id): id is Id<"contractVideos"> => id !== undefined)
            : []
        }
        onBack={handleBackToProjects}
        refetch={refetch}
        initialVersionId={initialVersionId}
        onNavigate={onNavigate}
      />
    );
  }

  // If no project is selected, show the project browser
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between"></div>

      {/* New Project Form */}
      {showNewProjectForm && (
        <Card className="p-4">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-primary">
                Project Title
              </label>
              <input
                type="text"
                value={newProjectTitle}
                onChange={(e) => setNewProjectTitle(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg"
                placeholder="e.g., App Feature Showcase"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-primary">
                Upload Video
              </label>
              <div className="mt-1 flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                <Upload className="mb-2 h-8 w-8 text-primary/60" />
                <p className="mb-1 text-sm font-medium">Upload your video</p>
                <p className="mb-4 text-xs text-primary/60">
                  {!newProjectTitle.trim()
                    ? "Enter a project title to continue"
                    : "Select a video file to upload"}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  className="hidden"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length > 0) {
                      handleCreateProject(files);
                    }
                  }}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isCreatingProject || !newProjectTitle.trim()}
                >
                  {isCreatingProject ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Select Video
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowNewProjectForm(false)}
                disabled={isCreatingProject}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Project Browser */}
      <ProjectBrowser
        projects={filteredProjects}
        selectedProjectId={selectedProjectId}
        onSelectProject={handleSelectProject}
        onCreateProject={() => setShowNewProjectForm(true)}
        isAdmin={isAdmin}
        onMergeProject={handleMergeProject}
      />

      {/* Merge Dialog */}
      {sourceProjectForMerge && (
        <MergeProjectDialog
          open={mergeDialogOpen}
          onOpenChange={(open) => {
            setMergeDialogOpen(open);
            if (!open) {
              setSourceProjectForMerge(null);
            }
          }}
          sourceProject={sourceProjectForMerge}
          allProjects={filteredProjects}
          onMerge={handleConfirmMerge}
          isMerging={isMerging}
        />
      )}
    </div>
  );
};
